/** The one list of keyboard shortcuts in the web app: what the cheat sheet shows and what the global listener binds.
 *  Pure, so the matching rules (Cmd or Ctrl, text fields, two-key sequences) are tested without a DOM. */

export type ShortcutGroupId = 'general' | 'go' | 'boq' | 'takeoff';

export const SHORTCUT_GROUPS: readonly { id: ShortcutGroupId; title: string }[] = [
  { id: 'general', title: 'General' },
  { id: 'go', title: 'Go to' },
  { id: 'boq', title: 'BOQ' },
  { id: 'takeoff', title: 'Takeoff' },
];

/** Shortcuts the global listener runs. Rows without an action are bound by their own surface and only listed here. */
export type GlobalShortcutAction = 'search' | 'new-project' | 'go-home' | 'go-projects' | 'show-shortcuts' | 'toggle-quantisai';

export interface ShortcutDefinition {
  id: string;
  label: string;
  group: ShortcutGroupId;
  /** Each entry is one way to press it. `mod` is Cmd or Ctrl (either works), `rail` the main rail's modifier. */
  keys: readonly (readonly string[])[];
  /** The keys of each entry are pressed one after the other, not held together. */
  sequence?: boolean;
  action?: GlobalShortcutAction;
  /** Also fires while typing, for a shortcut that has no meaning as text (closing QuantisAI from its own composer). */
  whileTyping?: boolean;
}

export const SHORTCUTS: readonly ShortcutDefinition[] = [
  { id: 'search', label: 'Search Quantis', group: 'general', keys: [['mod', 'K']], action: 'search' },
  { id: 'new-project', label: 'New project', group: 'general', keys: [['mod', 'Shift', 'O']], action: 'new-project' },
  { id: 'shortcuts', label: 'Show this list', group: 'general', keys: [['?'], ['mod', '/']], action: 'show-shortcuts' },
  { id: 'quantisai', label: 'Show or hide QuantisAI', group: 'general', keys: [['mod', '\\']], action: 'toggle-quantisai', whileTyping: true },
  { id: 'preferences', label: 'Open Preferences', group: 'general', keys: [['mod', ',']] },
  { id: 'sidebar', label: 'Show or hide the section list', group: 'general', keys: [['mod', 'B']] },
  { id: 'close', label: 'Close a panel or dialog', group: 'general', keys: [['Esc']] },
  { id: 'go-home', label: 'Home', group: 'go', keys: [['G', 'H']], sequence: true, action: 'go-home' },
  { id: 'go-projects', label: 'Projects', group: 'go', keys: [['G', 'P']], sequence: true, action: 'go-projects' },
  { id: 'rail', label: 'A main menu item (the first nine)', group: 'go', keys: [['rail', '1 to 9']] },
  { id: 'boq-select-all', label: 'Select every item in the bill', group: 'boq', keys: [['mod', 'A']] },
  { id: 'boq-delete', label: 'Delete the selected items', group: 'boq', keys: [['Delete'], ['Backspace']] },
  { id: 'boq-move', label: 'Move the selected items up or down', group: 'boq', keys: [['Alt', '↑'], ['Alt', '↓']] },
  { id: 'boq-clear', label: 'Clear the selection', group: 'boq', keys: [['Esc']] },
  { id: 'takeoff-area', label: 'Area tool', group: 'takeoff', keys: [['A']] },
  { id: 'takeoff-length', label: 'Length tool', group: 'takeoff', keys: [['L']] },
  { id: 'takeoff-count', label: 'Count tool', group: 'takeoff', keys: [['C']] },
  { id: 'takeoff-zoom', label: 'Zoom window tool', group: 'takeoff', keys: [['W']] },
  { id: 'takeoff-fit', label: 'Fit to view', group: 'takeoff', keys: [['F']] },
  { id: 'takeoff-pan', label: 'Pan while held', group: 'takeoff', keys: [['Space']] },
  { id: 'takeoff-finish', label: 'Finish the shape', group: 'takeoff', keys: [['Enter']] },
  { id: 'takeoff-undo-point', label: 'Remove the last point', group: 'takeoff', keys: [['Backspace']] },
  { id: 'takeoff-cancel', label: 'Cancel drawing', group: 'takeoff', keys: [['Esc']] },
  { id: 'takeoff-help', label: 'Every takeoff shortcut', group: 'takeoff', keys: [['?']] },
  { id: 'takeoff-copy', label: 'Copy or paste a takeoff between sheets', group: 'takeoff', keys: [['mod', 'C'], ['mod', 'V']] },
];

/** Keys two rows may share on purpose: the surface on screen claims it first and the global one waits. */
export const LAYERED_KEYS: readonly string[] = [
  // Takeoff and the BOQ cancel or clear first; the shell's Esc closes the panel only when they did not.
  'Esc',
  // Takeoff's own key list, which this sheet's ? gives way to while a takeoff is open.
  '?',
];

export interface ShortcutEnvironment {
  /** Cmd instead of Ctrl in the labels. */
  mac: boolean;
  /** The modifier the main rail's number shortcuts use (the browser must keep Cmd+1..9 for its tabs). */
  railModifier: 'ctrl' | 'meta';
}

export interface ShortcutRow {
  id: string;
  label: string;
  group: ShortcutGroupId;
  /** One list of key caps per way to press it. */
  keys: string[][];
  sequence: boolean;
}

export function buildShortcutRows(env: ShortcutEnvironment, options: { canAddProject?: boolean } = {}): ShortcutRow[] {
  const caps: Record<string, string> = {
    mod: env.mac ? 'Cmd' : 'Ctrl',
    rail: env.railModifier === 'meta' ? 'Cmd' : 'Ctrl',
    Alt: env.mac ? 'Option' : 'Alt',
  };
  return SHORTCUTS
    .filter((shortcut) => shortcut.action !== 'new-project' || options.canAddProject !== false)
    .map((shortcut) => ({
      id: shortcut.id,
      label: shortcut.label,
      group: shortcut.group,
      keys: shortcut.keys.map((combo) => combo.map((key) => caps[key] ?? key)),
      sequence: shortcut.sequence === true,
    }));
}

export interface KeyEventLike {
  key: string;
  code?: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  isComposing?: boolean;
  repeat?: boolean;
}

/** The letter or digit a key event names, read from the physical key so Option on a Mac and other layouts still match. */
function eventKey(event: KeyEventLike): string {
  const physical = /^(?:Key([A-Z])|Digit([0-9]))$/.exec(event.code ?? '');
  if (physical) return physical[1] ?? physical[2];
  return event.key.length === 1 ? event.key.toUpperCase() : event.key;
}

/** True when one held-together combination (`['mod', 'Shift', 'O']`, `['?']`, `['G']`) matches the event. */
export function comboMatches(event: KeyEventLike, combo: readonly string[]): boolean {
  if (event.isComposing) return false;
  const main = combo[combo.length - 1];
  const wantsMod = combo.includes('mod');
  // Exactly one of Cmd and Ctrl: both together belongs to other tools (Takeoff's Ctrl+Cmd+F).
  if (wantsMod ? event.metaKey === event.ctrlKey : event.metaKey || event.ctrlKey) return false;
  if (event.altKey !== combo.includes('Alt')) return false;
  // Shifted symbols (? on most layouts) arrive with Shift down, so only letters check it.
  if (main === '?') return event.key === '?';
  if (event.shiftKey !== combo.includes('Shift')) return false;
  if (main === '/' || main === ',' || main === '\\') return event.key === main || (main === '\\' && event.code === 'Backslash');
  return eventKey(event) === main.toUpperCase();
}

export interface TargetLike {
  tagName?: string;
  type?: string;
  isContentEditable?: boolean;
}

const NON_TEXT_INPUTS = new Set(['button', 'checkbox', 'color', 'file', 'image', 'radio', 'range', 'reset', 'submit']);

/** Text entry owns every key typed into it, so no shortcut fires from a field, a select or rich text. */
export function isTextEntryTarget(target: TargetLike | null | undefined): boolean {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName?.toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  return tag === 'input' && !NON_TEXT_INPUTS.has((target.type ?? 'text').toLowerCase());
}

export const SEQUENCE_TIMEOUT_MS = 1000;

/** Matches key events against the bound shortcuts, remembering the first key of a sequence for a second. */
export function createShortcutMatcher(definitions: readonly ShortcutDefinition[] = SHORTCUTS) {
  const bound = definitions.filter((definition) => definition.action);
  let pending: { key: string; at: number } | null = null;

  return {
    /** The shortcut this event completes, if any. `now` is injectable for tests. */
    match(event: KeyEventLike, now: number = Date.now()): ShortcutDefinition | null {
      if (event.isComposing || event.repeat) return null;
      const previous = pending && now - pending.at <= SEQUENCE_TIMEOUT_MS ? pending.key : null;
      pending = null;
      for (const definition of bound) {
        if (!definition.sequence) {
          if (definition.keys.some((combo) => comboMatches(event, combo))) return definition;
          continue;
        }
        const hit = definition.keys.some((combo) => previous === combo[0] && comboMatches(event, [combo[1]]));
        if (hit) return definition;
      }
      const startsSequence = bound.some((definition) =>
        definition.sequence && definition.keys.some((combo) => comboMatches(event, [combo[0]])),
      );
      if (startsSequence) pending = { key: eventKey(event), at: now };
      return null;
    },
    reset() {
      pending = null;
    },
  };
}

/** Pairs of rows that claim the same keys, other than the deliberate layers. Used by the tests to keep the set consistent. */
export function findShortcutConflicts(definitions: readonly ShortcutDefinition[] = SHORTCUTS): [string, string][] {
  const owners = new Map<string, ShortcutDefinition[]>();
  for (const definition of definitions) {
    for (const combo of definition.keys) {
      const id = `${definition.sequence ? 'seq:' : ''}${combo.join('+')}`;
      owners.set(id, [...(owners.get(id) ?? []), definition]);
    }
  }
  const conflicts: [string, string][] = [];
  for (const [combo, rows] of owners) {
    if (rows.length < 2 || LAYERED_KEYS.includes(combo)) continue;
    for (let i = 0; i < rows.length; i += 1) {
      for (let j = i + 1; j < rows.length; j += 1) {
        // A surface's keys only collide with the global set: BOQ and Takeoff never share a screen.
        const scoped = (row: ShortcutDefinition) => row.group === 'boq' || row.group === 'takeoff';
        if (scoped(rows[i]) && scoped(rows[j]) && rows[i].group !== rows[j].group) continue;
        conflicts.push([rows[i].id, rows[j].id]);
      }
    }
  }
  return conflicts;
}
